// The zoomed view's "split by" choices beyond its four built-in ones: every real-time filter (country, system, browser, device, traffic source, search engine, social network, referral
// link, visitor type). Pure (no canvas, no DOM): the engine in densityCloudService.ts feeds these the visitors on the page and draws what comes back.

export interface ZoomFilter {
  /** the visitor field the filter groups by, as the sidebar's filters name it */
  menuType: string;
  label: string;
}

export const ZOOM_FILTERS: ZoomFilter[] = [
  { menuType: "country_name", label: "Country" },
  { menuType: "os", label: "Operating System" },
  { menuType: "browser", label: "Browser" },
  { menuType: "device", label: "Device Type" },
  { menuType: "traffic_source", label: "Traffic Source" },
  { menuType: "search_engine", label: "Search Engine" },
  { menuType: "social_network", label: "Social Network" },
  { menuType: "referrer", label: "Referral Link" },
  { menuType: "visitor_category", label: "Visitor Type" },
];

const PREFIX = "f:";
export const filterDimId = (menuType: string): string => `${PREFIX}${menuType}`;
/** the menuType inside a filter grouping's id, or null for any other id (and for a filter the zoomed view does not offer) */
export const parseFilterDim = (id: string | null | undefined): string | null => {
  if (typeof id !== "string" || !id.startsWith(PREFIX)) {
    return null;
  }
  const menuType = id.slice(PREFIX.length);

  return ZOOM_FILTERS.some((f) => f.menuType === menuType) ? menuType : null;
};

/** "23%" for a region's share of the page; "<1%" for a group that exists but rounds to nothing; nothing at all when there is nobody to take a share of */
export const formatShare = (count: number, total: number): string => {
  if (!(total > 0) || !(count >= 0)) {
    return "";
  }
  const pct = (100 * count) / total;
  if (count > 0 && pct < 0.5) {
    return "<1%";
  }

  return `${Math.round(pct)}%`;
};

/**
 * The order the regions are laid out in: the groups with the most visitors first (so the busiest sit at the top left, and are the first thing seen when the layout is bigger than the
 * view), then the rest of the known universe in its own order. Once chosen it NEVER reshuffles: a group that appears later is added at the end, so a region only ever moves when
 * a brand-new group shows up, not because counts changed.
 */
export const initialCategoryOrder = (counts: Record<string, number>, universe: string[] = []): string[] => {
  const seen = Object.keys(counts).filter((k) => counts[k] > 0);
  seen.sort((a, b) => counts[b] - counts[a] || (a < b ? -1 : a > b ? 1 : 0));
  const rest = universe.filter((u) => !seen.includes(u));

  return [...seen, ...rest];
};

export const appendNewCategories = (known: string[], counts: Record<string, number>): string[] => {
  const fresh = Object.keys(counts).filter((k) => counts[k] > 0 && !known.includes(k));
  if (fresh.length === 0) {
    return known;
  }
  fresh.sort((a, b) => counts[b] - counts[a] || (a < b ? -1 : a > b ? 1 : 0));

  return [...known, ...fresh];
};

export interface RegionGrid {
  cols: number;
  rows: number;
  /** each region's disc radius */
  R: number;
  /** a cell's width and height (the height includes the room for the label above the disc) */
  cw: number;
  ch: number;
  /** true when the groups could not all fit at a readable size: the grid is then bigger than the view and the person pans to see the rest */
  overflow: boolean;
  /** how far the grid runs past the bottom of the area, in the area's own units (0 unless it overflows) */
  extra: number;
}

export interface RegionGridOptions {
  titleH: number;
  cellPad: number;
  /** the smallest disc worth drawing; below it the layout overflows instead of shrinking further (only when allowOverflow) */
  minR: number;
  /** the old limits for a grouping that always fits: the radius is clamped to [fitMin, fitMax] */
  fitMin: number;
  fitMax: number;
  allowOverflow: boolean;
  /** an overflowing grid's cells are at least this wide (room for a long name under its disc, e.g. a referring link); default: just the disc and its padding */
  minCellW?: number;
}

/**
 * Every group gets a cell of an even grid over the area; the grid shape (columns x rows) is the one that gives the biggest discs. When even that is smaller than minR - too many groups
 * for the area (every country, every referring site) - the discs stay at minR, as many columns as fit across the area, and the rows run on below it: the view pans.
 */
export const computeRegionGrid = (n: number, bw: number, bh: number, o: RegionGridOptions): RegionGrid => {
  let best = { cols: 1, rows: Math.max(1, n), R: -Infinity };
  for (let cols = 1; cols <= Math.max(1, n); cols++) {
    const rows = Math.ceil(Math.max(1, n) / cols);
    const R = Math.min(bw / cols / 2 - o.cellPad, (bh / rows - o.titleH) / 2 - o.cellPad);
    if (R > best.R) {
      best = { cols, rows, R };
    }
  }
  if (o.allowOverflow && best.R < o.minR) {
    const cols = Math.max(1, Math.floor(bw / Math.max(2 * o.minR + 2 * o.cellPad, o.minCellW ?? 0)));
    const rows = Math.ceil(Math.max(1, n) / cols);
    const ch = o.titleH + 2 * o.minR + 2 * o.cellPad;

    return { cols, rows, R: o.minR, cw: bw / cols, ch, overflow: rows * ch > bh, extra: Math.max(0, rows * ch - bh) };
  }
  const R = Math.max(o.fitMin, Math.min(o.fitMax, best.R));

  return { cols: best.cols, rows: best.rows, R, cw: bw / best.cols, ch: bh / best.rows, overflow: false, extra: 0 };
};
